import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import type { GameSettings } from '../settings';

interface SettingsDialogProps {
  open: boolean;
  settings: GameSettings;
  hasActiveRun: boolean;
  onChange: (settings: GameSettings) => void;
  onClose: () => void;
  onAbandon: () => void;
}

export function SettingsDialog({
  open,
  settings,
  hasActiveRun,
  onChange,
  onClose,
  onAbandon,
}: SettingsDialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  const abandonTrigger = useRef<HTMLButtonElement>(null);
  const keepPlaying = useRef<HTMLButtonElement>(null);
  const abandonConfirmationWasOpen = useRef(false);
  const [confirmAbandon, setConfirmAbandon] = useState(false);

  useEffect(() => {
    if (!open) return;
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      returnFocus.current?.focus();
    };
  }, [open]);

  useEffect(() => {
    if (!open) setConfirmAbandon(false);
  }, [open]);

  useEffect(() => {
    if (!open) {
      abandonConfirmationWasOpen.current = false;
      return;
    }
    if (confirmAbandon) {
      abandonConfirmationWasOpen.current = true;
      keepPlaying.current?.focus();
    } else if (abandonConfirmationWasOpen.current) {
      abandonConfirmationWasOpen.current = false;
      abandonTrigger.current?.focus();
    }
  }, [confirmAbandon, open]);

  if (!open) return null;

  const update = (patch: Partial<GameSettings>) => onChange({ ...settings, ...patch });
  const trapFocus = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (confirmAbandon) setConfirmAbandon(false);
      else onClose();
      return;
    }
    if (event.key !== 'Tab' || !panel.current) return;
    const focusable = [...panel.current.querySelectorAll<HTMLElement>(
      'a[href], button, input, select, textarea, summary, [tabindex]:not([tabindex="-1"])',
     )].filter((element) => {
      if (element.hasAttribute('disabled') || element.closest('[hidden], [inert]')) return false;
      // Links inside collapsed details are not keyboard targets.
      const collapsed = element.closest('details:not([open])');
      return !collapsed || element === collapsed.querySelector(':scope > summary');
    });
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div className="settings-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <div
        className="settings-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        ref={panel}
        tabIndex={-1}
        onKeyDown={trapFocus}
      >
        <header role="presentation">
          <span><small>Cathedris</small><h2 id="settings-title">Settings & field guide</h2></span>
          <button type="button" className="settings-close" aria-label="Close settings" onClick={onClose}>×</button>
        </header>

        <section aria-labelledby="audio-settings-title">
          <h3 id="audio-settings-title">Audio</h3>
          <label className="setting-check">
            <input type="checkbox" checked={settings.muted} onChange={(event) => update({ muted: event.target.checked })} />
            <span><strong>Mute sound</strong><small>Silence all synthesized game feedback.</small></span>
          </label>
          <label className="setting-range">
            <span><strong>Master volume</strong><output>{Math.round(settings.masterVolume * 100)}%</output></span>
            <input
              type="range"
              aria-label="Master volume"
              min="0"
              max="1"
              step="0.05"
              value={settings.masterVolume}
              disabled={settings.muted}
              onChange={(event) => update({ masterVolume: Number(event.target.value) })}
            />
          </label>
        </section>

        <section aria-labelledby="display-settings-title">
          <h3 id="display-settings-title">Display & comfort</h3>
          {([
            ['reducedMotion', 'Reduce motion', 'Stops atmospheric and targeting animation.'],
            ['largeText', 'Larger text', 'Raises the interface text scale.'],
            ['highContrast', 'Higher contrast', 'Strengthens text and panel borders.'],
          ] as const).map(([key, label, description]) => (
            <label className="setting-check" key={key}>
              <input type="checkbox" checked={settings[key]} onChange={(event) => update({ [key]: event.target.checked })} />
              <span><strong>{label}</strong><small>{description}</small></span>
            </label>
          ))}
        </section>

        <details className="field-guide">
          <summary>How to play</summary>
          <dl>
            <div><dt>Energy</dt><dd>Refreshes each turn. A card’s top-left number is its cost.</dd></div>
            <div><dt>Heat</dt><dd>Stoke for stronger effects, then Temper it. End at 6 and take 3 unblocked damage.</dd></div>
            <div><dt>Gear</dt><dd>Powerful cards with Durability that persists between fights. Broken Gear needs repair.</dd></div>
            <div><dt>Intents</dt><dd>Enemies show their next move. Their actions resolve from left to right.</dd></div>
            <div><dt>Block</dt><dd>Absorbs incoming damage, then expires before your next turn.</dd></div>
            <div><dt>Route</dt><dd>Balance fights, events, shops, and forges before each act guardian.</dd></div>
            <div><dt>Strength</dt><dd>Adds 1 damage per stack to every hit.</dd></div>
            <div><dt>Dexterity</dt><dd>Adds 1 Block per stack whenever Block is gained.</dd></div>
            <div><dt>Weak</dt><dd>Reduces outgoing damage by 25% while active.</dd></div>
            <div><dt>Vulnerable</dt><dd>Increases incoming damage by 50% while active.</dd></div>
            <div><dt>Burning</dt><dd>Deals damage equal to its stacks before the afflicted enemy acts, then loses 1 stack.</dd></div>
            <div><dt>Thorns</dt><dd>Returns damage equal to its stacks after each enemy hit.</dd></div>
          </dl>
        </details>

        <details className="credits-panel">
          <summary>Credits & provenance</summary>
          <p><a href="https://www.worldanvil.com/w/cathedris-stormbril" target="_blank" rel="noreferrer">Read Cathedris by Stormbril</a></p>
          <p>Genesis Ichor is cool seafoam/aquamarine and reacts violently with water. Extraction rigs carry raw Ichor; a Catalurgist’s implanted Thymalladus refines it. The coolant circuit is separate. Heat is this game’s abstraction of Burn, not a canonical six-step scale. Iron is a gameplay salvage resource, not a claim about Cathedris currency.</p>
          <p>Cathedris is created by Devon “Stormbril” Ruscheinski. The Bellrose Line is an independent, unofficial game adaptation; no endorsement is implied. The expedition, characters, devices, and three-act plot are inventions for this prototype.</p>
          <p>New artwork uses Codex imagegen and Blender source scenes. Prompts, references, source files and the asset manifest are recorded in the repository. No creator illustrations or quarantined legacy media are included.</p>
        </details>

        {hasActiveRun ? (
          <section className="danger-zone" aria-labelledby="run-controls-title">
            <h3 id="run-controls-title">Run controls</h3>
            {confirmAbandon ? (
              <div
                className="abandon-confirmation"
                role="group"
                aria-labelledby="abandon-confirmation-title"
                aria-describedby="abandon-confirmation-description"
              >
                <strong id="abandon-confirmation-title">Abandon this expedition?</strong>
                <p id="abandon-confirmation-description">This permanently deletes the current expedition save.</p>
                <span>
                  <button ref={keepPlaying} type="button" className="text-action" onClick={() => setConfirmAbandon(false)}>Keep playing</button>
                  <button type="button" className="danger-action" onClick={onAbandon}>Abandon expedition</button>
                </span>
              </div>
            ) : (
              <button ref={abandonTrigger} type="button" className="text-action" onClick={() => setConfirmAbandon(true)}>Abandon current expedition</button>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
